import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { CrmBadge, CrmButton, CrmTooltip } from '../components/ui';
import { CrmEntityListPage } from '../components/listing';
import NotificationSnackbar from '../components/ui/NotificationSnackbar';
import { listOrders } from '../services/orderService';
import { formatCurrency } from '../utils/currencyUtils';
import { formatDate, toInputDateValue } from '../utils/dateFormatter';
import { formatStatusLabel, normalizeStatusValue } from '../utils/statusFormatter';
import { useSettings } from '../context/SettingsContext';
import useAutoRefresh from '../hooks/useAutoRefresh';

const sourceLabel = (row) => {
  const source = String(row?.source || row?.source_type || '').trim().toUpperCase();
  if (source === 'WEBSITE' || source === 'FRONTEND_ORDER' || source === 'WEBSITE_ORDER') return 'Website';
  if (source.includes('QUOT')) return 'Quotation';
  if (source === 'CRM') return 'CRM';
  return source ? source.replace(/_/g, ' ') : 'Manual';
};

const statusTone = (value) => ({
  completed: 'success',
  ready: 'success',
  confirmed: 'info',
  in_fulfilment: 'info',
  awaiting_payment: 'warning',
  awaiting_confirmation: 'warning',
  cancelled: 'danger',
  draft: 'neutral',
}[normalizeStatusValue(value)] || 'neutral');

const paymentTone = (value) => ({
  paid: 'success',
  partially_paid: 'warning',
  pending: 'warning',
  unpaid: 'neutral',
  failed: 'danger',
  cancelled: 'danger',
  refunded: 'info',
  partially_refunded: 'info',
}[normalizeStatusValue(value)] || 'neutral');

const fulfilmentTone = (value) => ({
  completed: 'success',
  ready: 'success',
  ready_for_pickup: 'success',
  processing: 'info',
  out_for_delivery: 'info',
  shipped: 'info',
  cancelled: 'danger',
  not_started: 'neutral',
}[normalizeStatusValue(value)] || 'neutral');

export default function Orders() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const load = useCallback(async ({ isAutoRefresh = false } = {}) => {
    if (!isAutoRefresh) setLoading(true);
    try {
      setOrders(await listOrders());
      setError('');
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'Failed to load orders.');
    } finally {
      if (!isAutoRefresh) setLoading(false);
    }
  }, []);

  useAutoRefresh(load, { intervalMs: 20000 });

  const columns = useMemo(() => [
    { id: 'order_number', label: 'Order #', width: 175, minWidth: 125, maxWidth: 260, hideable: false, defaultPinned: true, pinnable: true, accessor: (row) => row.order_number || `Order ${row.id}` },
    { id: 'customer_name', label: 'Customer', width: 220, minWidth: 120, maxWidth: 360, accessor: (row) => row.customer_name || '—' },
    { id: 'source', label: 'Source', width: 130, minWidth: 90, maxWidth: 180, filterable: true, accessor: sourceLabel },
    { id: 'storefront', label: 'Storefront', width: 180, minWidth: 95, maxWidth: 280, filterable: true, accessor: (row) => row.storefront_name || '—' },
    { id: 'fulfilment_mode', label: 'Mode', width: 125, minWidth: 85, maxWidth: 180, filterable: true, accessor: (row) => formatStatusLabel(row.fulfilment_mode || '—') },
    { id: 'status', label: 'Order Status', width: 160, minWidth: 115, maxWidth: 230, filterable: true, accessor: (row) => normalizeStatusValue(row.status), renderCell: (row) => <CrmBadge tone={statusTone(row.status)}>{formatStatusLabel(row.status)}</CrmBadge> },
    { id: 'payment_status', label: 'Payment', width: 145, minWidth: 105, maxWidth: 210, filterable: true, accessor: (row) => normalizeStatusValue(row.payment_status), renderCell: (row) => <CrmBadge tone={paymentTone(row.payment_status)}>{formatStatusLabel(row.payment_status)}</CrmBadge> },
    { id: 'fulfilment_status', label: 'Fulfilment', width: 155, minWidth: 105, maxWidth: 220, filterable: true, accessor: (row) => normalizeStatusValue(row.fulfilment_status || 'not_started'), renderCell: (row) => <CrmBadge tone={fulfilmentTone(row.fulfilment_status || 'not_started')}>{formatStatusLabel(row.fulfilment_status || 'not_started')}</CrmBadge> },
    { id: 'grand_total', label: 'Total', width: 150, minWidth: 110, maxWidth: 220, type: 'currency', accessor: (row) => Number(row.grand_total || row.total_amount || 0), renderCell: (row) => formatCurrency(row.grand_total || row.total_amount || 0, settings?.currency_code) },
    { id: 'created_at', label: 'Created', width: 145, minWidth: 110, maxWidth: 210, type: 'date', accessor: (row) => row.created_at, renderCell: (row) => formatDate(row.created_at) || '—' },
    {
      id: 'row_actions', label: 'Actions', width: 88, minWidth: 88, maxWidth: 104, hideable: false, sortable: false, searchable: false, className: 'crm-data-table__actions-cell',
      renderCell: (row) => (
        <span className="crm-row-actions">
          <CrmTooltip content="Open order"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => navigate(`/orders/${row.id}`)} aria-label="Open order"><VisibilityOutlinedIcon /></CrmButton></CrmTooltip>
        </span>
      ),
    },
  ], [settings?.currency_code]);

  const filters = useMemo(() => [
    { id: 'created_at', columnId: 'created_at', label: 'Created', type: 'date-range', quick: true, getValue: (row) => row.created_at },
    { id: 'source', columnId: 'source', label: 'Source', type: 'select', quick: true, options: ['Website', 'Quotation', 'CRM', 'Manual'].map((value) => ({ value, label: value })), getValue: sourceLabel },
    { id: 'status', columnId: 'status', label: 'Order Status', type: 'select', quick: true, options: ['DRAFT','AWAITING_PAYMENT','AWAITING_CONFIRMATION','CONFIRMED','IN_FULFILMENT','READY','OUT_FOR_DELIVERY','COMPLETED','CANCELLED'].map((value) => ({ value: normalizeStatusValue(value), label: formatStatusLabel(value) })), getValue: (row) => normalizeStatusValue(row.status) },
    { id: 'payment_status', columnId: 'payment_status', label: 'Payment', type: 'select', quick: true, options: ['UNPAID','PENDING','PARTIALLY_PAID','PAID','FAILED','CANCELLED','PARTIALLY_REFUNDED','REFUNDED'].map((value) => ({ value: normalizeStatusValue(value), label: formatStatusLabel(value) })), getValue: (row) => normalizeStatusValue(row.payment_status) },
    { id: 'fulfilment_mode', columnId: 'fulfilment_mode', label: 'Mode', type: 'select', quick: false, options: ['DELIVERY','PICKUP','SHIPPING'].map((value) => ({ value, label: formatStatusLabel(value) })), getValue: (row) => String(row.fulfilment_mode || '').toUpperCase() },
  ], []);

  const todayFilter = useMemo(() => {
    const today = toInputDateValue(new Date());
    return { created_at: { startDate: today, endDate: today } };
  }, []);

  const summary = useMemo(() => [
    { label: 'Orders', value: orders.length },
    { label: 'Awaiting action', value: orders.filter((row) => ['AWAITING_PAYMENT','AWAITING_CONFIRMATION','CONFIRMED'].includes(String(row.status || '').toUpperCase())).length },
    { label: 'In fulfilment', value: orders.filter((row) => ['IN_FULFILMENT','READY','OUT_FOR_DELIVERY'].includes(String(row.status || '').toUpperCase())).length },
    { label: 'Order value', value: formatCurrency(orders.reduce((sum, row) => sum + Number(row.grand_total || row.total_amount || 0), 0), settings?.currency_code) },
  ], [orders, settings?.currency_code]);

  const exportRows = (rows) => {
    try {
      const data = rows.map((row) => ({
        'Order #': row.order_number,
        Customer: row.customer_name || '',
        Source: sourceLabel(row),
        Storefront: row.storefront_name || '',
        'Sales Channel': row.sales_channel_name || '',
        Mode: formatStatusLabel(row.fulfilment_mode),
        Status: formatStatusLabel(row.status),
        Payment: formatStatusLabel(row.payment_status),
        Fulfilment: formatStatusLabel(row.fulfilment_status || 'NOT_STARTED'),
        Total: Number(row.grand_total || row.total_amount || 0),
        Currency: settings?.currency_code || 'INR',
        Created: formatDate(row.created_at),
      }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), 'Orders');
      XLSX.writeFile(workbook, 'orders.xlsx');
    } catch (exportError) {
      setNotification({ open: true, message: 'Failed to export orders.', severity: 'error' });
    }
  };

  return (
    <div className="contacts-page">
      <CrmEntityListPage
        moduleKey="orders"
        title="Orders"
        itemLabel="order"
        items={orders}
        columns={columns}
        filterDefinitions={filters}
        initialSort={{ columnId: 'created_at', direction: 'desc' }}
        initialFilters={todayFilter}
        loading={loading}
        error={error}
        onRetry={load}
        onRefresh={load}
        onExport={exportRows}
        onExportSelected={exportRows}
        onRowClick={(row) => navigate(`/orders/${row.id}`)}
        summaryItems={summary}
        emptyTitle="No orders yet"
        emptyMessage="Storefront purchases, approved quotations and other commercial order sources will appear here."
      />
      <NotificationSnackbar {...notification} onClose={() => setNotification((current) => ({ ...current, open: false }))} />
    </div>
  );
}
